<template>
  <div class="search-result-table">
    <div v-if="editstate" class="jps-srt-edit">
      <button
        type="button"
        class="jps-round-button jps-orange"
        @click="editColumn()"
      >
        <BaseIcon name="mdi/pen-plus" size="size-6" />
        {{ lss('列の追加', 'Add column') }}
      </button>
      <button
        v-if="editstate.cs.columns.length >= 2"
        type="button"
        class="jps-round-button jps-orange"
        @click="sortColumn()"
      >
        <BaseIcon name="interface/sort" size="size-6" />
        {{ lss('列の並び替え', 'Sort column') }}
      </button>
      <CsColumnModal ref="csColumnModal" />
      <UiSortModal ref="sortModal" :renderer="(f) => ls(f.header)" />
    </div>

    <UiTable :data="ss.state.result.list" :columns="columnDefs" class="ui-sortable-search-result-table">
      <template #header="{ col, colIndex, label }">
        <div class="search-result-table-header-inner">
          {{ label }}
          <SearchSorter
            class="is-hidden-mobile"
            :ss="ss"
            :field="col.sort"
            is-item
          />
          <template v-if="editstate && col.key !== 'title' && col.key !== 'fav'">
            <UiButtonIcon
              tag="button"
              type="button"
              class="jps-icon-button jps-orange"
              icon="interface/pencil"
              icon-size="size-6"
              @click="editColumn(col)"
            />
            <UiButtonIcon
              tag="button"
              type="button"
              class="jps-icon-button jps-orange"
              icon="interface/close-circle"
              icon-size="size-6"
              @click="removeColumn(col)"
            />
          </template>
        </div>
      </template>
      <template #column-title="{ row, path }">
        <BaseLink
          :nolink="isWebParts"
          @click.native="emits('show', row)"
          :cache="{ type: 'item', data: row }"
          :to="itemTo(row)"
          >{{ getTitle(row, path) }}</BaseLink
        >
      </template>
      <template #column-db="{ row }">
        <UiDbSpan :id="row.common.database" :nolink="isWebParts" />
      </template>
      <template #column-database_site="{ row }">
        <BaseLink v-if="row.common.linkUrl" :to="row.common.linkUrl" icon-size="size-5">
          <UiDbSpan :id="row.common.database" tag="span" />
        </BaseLink>
        <UiDbSpan v-else :id="row.common.database" tag="span" />
      </template>
      <template #column-provider="{ row }">
        <UiOrgProviderSpan
          tag="BaseLink"
          :id="row.common.provider"
          :csid="ss.state.searchdef.id"
          :nolink="isWebParts"
        />
      </template>
      <template #column-organization="{ row }">
        <BaseLink :to="'/organization/' + row.common.ownerOrg" :nolink="isWebParts">
          <UiOrgSpan :id="row.common.ownerOrg" />
        </BaseLink>
      </template>
      <template #column-code="{ row }">
        <UiCodeSpan
          v-for="value in row.values"
          :id="row.def.code"
          :key="value"
          :codevalue="value"
        />
      </template>
      <template #column-fav="{ row }">
        <UiInteractPanel type="item" :data="row" />
      </template>
    </UiTable>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { CsColumnModal, UiSortModal } from '#components'
import { useSearchResult } from '~/components/ui/item/search/useSearchResult'
import type { ColumnDef } from '~/components/ui/UiTable.vue'

interface ColumnDefExt extends ColumnDef {
  csCol?: CustomSearchColumn
}

const { isWebParts } = useWebParts()
const { ls, lss } = useI18n()

interface Props {
  test?: boolean
  nofav?: boolean
  columns: CustomSearchColumn[]
  editstate: any
  ss: SearchStore<Item>
}
const props = withDefaults(defineProps<Props>(), {
  test: false,
  nofav: false
})

const emits = defineEmits(['show'])

const { itemTo } = useSearchResult(props.ss, props.test)

const columnDefs = computed(() => {
  let cols: ColumnDef[] =
    props.columns == null
      ? []
      : props.columns
        .filter(col => col.type !== 'title')
        .map(col => {
          if (col.type === 'database') {
            return {
              label: () => ls(col.header),
              class: 'jps-srt-db',
              key: 'db',
              csCol: col,
            }
          }
          if (col.type === 'database_site') {
            return {
              label: () => ls(col.header),
              class: 'jps-srt-db',
              key: 'database_site',
              csCol: col,
            }
          }
          if (col.type === 'provider') {
            return {
              label: () => ls(col.header),
              class: 'jps-srt-provider',
              key: 'provider',
              csCol: col,
            }
          }
          if (col.type === 'organization') {
            return {
              label: () => ls(col.header),
              class: 'jps-srt-provider',
              key: 'organization',
              csCol: col,
            }
          }
          if (col.type === 'code') {
            return {
              label: () => ls(col.header),
              key: 'code',
              path: col.fields,
              code: col.codeKey,
              csCol: col,
            }
          }
          return {
            label: () => ls(col.header),
            firstOnly: true,
            path: col.fields,
            sort: col.fields[0],
            csCol: col,
          }
        })
  cols.unshift({
    label: () => ls({ ja: '名称/タイトル', en: 'Title' }),
    path: ['common.title'],
    class: 'jps-srt-title',
    key: 'title',
    sort: 'common.title',
  })
  if (!props.nofav) {
    cols.push({
      key: 'fav',
      class: 'jps-srt-fav',
    })
  }
  return cols
})

const getTitle = (item: any, fields: string[]) => {
  let titles = deepFind3(item, fields)
  if (titles.length > 0) return titles.join(',')
  return ls(dapLangGlobal.noTitle)
}

const csColumnModal = ref<InstanceType<typeof CsColumnModal>>()
const editColumn = async (target?: ColumnDefExt) => {
  try {
    let column = await csColumnModal.value.openAwait({
      editstate: props.editstate,
      column: target ? target.csCol : null
    })
    if (target) {
      updateToArray(props.editstate.cs.columns, target.csCol, column)
    } else {
      props.editstate.cs.columns.push(column)
    }
  } catch (cancel) {}
}

const sortModal = ref<InstanceType<typeof UiSortModal>>()
const sortColumn = async () => {
  try {
    let sorted: any[] = await sortModal.value.openAwait(
      props.editstate.cs.columns
    )
    if (!sorted) return
    props.editstate.cs.columns = sorted
  } catch (cancel) {}
}

const removeColumn = (f: ColumnDefExt) => {
  removeFromArray(props.editstate.cs.columns, f.csCol)
  props.editstate.snapshot()
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.search-result-table {
  overflow-x: auto;
  padding-bottom: 1rem;

  .jps-srt-edit {
    display: flex;
    gap: 1rem;
  }

  .jps-srt-title {
    width: 30%;
  }
  .jps-srt-provider {
    width: 10%;
  }
  .jps-srt-db {
    width: 10%;
  }
  .jps-srt-fav {
    width: 1rem;
  }
}
</style>
